import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FiLock, FiPercent, FiPlus, FiShoppingBag, FiTrash2, FiTrendingDown, FiTrendingUp } from 'react-icons/fi';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { ImageField } from '../../components/ImageField';
import { Input } from '../../components/Input';
import { Kpi } from '../../components/Kpi';
import { MoedaInput } from '../../components/MoedaInput';
import { Select } from '../../components/Select';
import { criarCategoria, listarCategorias } from '../../api/categorias';
import { listarMateriasPrimas } from '../../api/materiaPrima';
import { buscarProduto, criarProduto, DadosProduto, salvarFichaTecnica, atualizarProduto } from '../../api/produtos';
import { Categoria, MateriaPrima } from '../../types';
import { lerArquivoComoDataUrl } from '../../utils/arquivos';
import { formatarMoeda } from '../../utils/formatadores';

const FORM_INICIAL: DadosProduto = {
  nome: '',
  descricao: '',
  categoria_id: '',
  preco_venda: '',
  imagem_base64: null,
  remover_imagem: false,
};

interface ItemFichaForm {
  materia_prima_id: number | string;
  quantidade: number | string;
}

function arredondar(valor: number) {
  return Math.round(valor * 100) / 100;
}

export function ProdutoForm() {
  const { id } = useParams<{ id: string }>();
  const editando = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState<DadosProduto>(FORM_INICIAL);
  const [imagemAtual, setImagemAtual] = useState<string | null>(null);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [materiasPrimas, setMateriasPrimas] = useState<MateriaPrima[]>([]);
  const [fichaTecnica, setFichaTecnica] = useState<ItemFichaForm[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [salvandoFicha, setSalvandoFicha] = useState(false);

  useEffect(() => {
    listarCategorias().then(setCategorias);
    listarMateriasPrimas().then(setMateriasPrimas);

    if (editando && id) {
      buscarProduto(id).then((dados) => {
        setForm({
          nome: dados.nome,
          descricao: dados.descricao || '',
          categoria_id: dados.categoria_id ?? '',
          preco_venda: dados.preco_venda,
          imagem_base64: null,
          remover_imagem: false,
        });
        setImagemAtual(dados.imagem || null);
        setFichaTecnica(
          (dados.ficha_tecnica || []).map((item) => ({
            materia_prima_id: item.materia_prima_id,
            quantidade: Number(item.quantidade),
          }))
        );
      });
    }
  }, [id, editando]);

  const cmvAoVivo = useMemo(() => {
    return fichaTecnica.reduce((soma, item) => {
      const materiaPrima = materiasPrimas.find((mp) => String(mp.id) === String(item.materia_prima_id));
      if (!materiaPrima || !item.quantidade) return soma;
      return soma + Number(item.quantidade) * Number(materiaPrima.custo_unitario);
    }, 0);
  }, [fichaTecnica, materiasPrimas]);

  const precoVendaAoVivo = Number(form.preco_venda) || 0;
  const margemLucroAoVivo = arredondar(precoVendaAoVivo - cmvAoVivo);
  const margemPercentualAoVivo = precoVendaAoVivo > 0 ? arredondar(((precoVendaAoVivo - cmvAoVivo) / precoVendaAoVivo) * 100) : null;

  function handleChange(campo: keyof DadosProduto) {
    return (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((atual) => ({ ...atual, [campo]: event.target.value }));
  }

  async function handleNovaCategoria() {
    const { value: nome } = await Swal.fire({
      title: 'Nova categoria',
      input: 'text',
      inputLabel: 'Nome da categoria',
      showCancelButton: true,
      confirmButtonText: 'Criar',
      cancelButtonText: 'Cancelar',
    });

    if (!nome) return;

    const categoria = await criarCategoria({ nome });
    setCategorias((atual) => [...atual, categoria]);
    setForm((atual) => ({ ...atual, categoria_id: categoria.id }));
  }

  async function handleImagemProduto(file: File | null) {
    if (!file) return;
    try {
      const imagem = await lerArquivoComoDataUrl(file);
      setForm((atual) => ({ ...atual, imagem_base64: imagem, remover_imagem: false }));
    } catch (error: any) {
      Swal.fire({ icon: 'error', title: 'Nao foi possivel carregar a imagem', text: error.message });
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSalvando(true);

    try {
      if (editando && id) {
        await atualizarProduto(id, form);
        Swal.fire({ icon: 'success', title: 'Produto atualizado!', timer: 1200, showConfirmButton: false });
      } else {
        const novoProduto = await criarProduto(form);
        navigate(`/produtos/${novoProduto.id}/editar`);
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Nao foi possivel salvar',
        text: error.response?.data?.erro || 'Verifique os dados informados.',
      });
    } finally {
      setSalvando(false);
    }
  }

  function adicionarItemFicha() {
    setFichaTecnica((atual) => [...atual, { materia_prima_id: '', quantidade: '' }]);
  }

  function atualizarItemFicha(index: number, campo: keyof ItemFichaForm, valor: string) {
    setFichaTecnica((atual) => atual.map((item, i) => (i === index ? { ...item, [campo]: valor } : item)));
  }

  function removerItemFicha(index: number) {
    setFichaTecnica((atual) => atual.filter((_, i) => i !== index));
  }

  async function handleSalvarFicha() {
    if (!id) return;
    setSalvandoFicha(true);

    try {
      const itensValidos = fichaTecnica
        .filter((item) => item.materia_prima_id && item.quantidade)
        .map((item) => ({ materia_prima_id: Number(item.materia_prima_id), quantidade: Number(item.quantidade) }));

      await salvarFichaTecnica(id, itensValidos);
      Swal.fire({ icon: 'success', title: 'Ficha tecnica salva!', timer: 1200, showConfirmButton: false });
    } finally {
      setSalvandoFicha(false);
    }
  }

  return (
    <AppLayout>
      <div className="lizzi-cabecalho-pagina">
        <h1>
          <span className="lizzi-icone-titulo">
            <FiShoppingBag size={18} />
          </span>
          {editando ? 'Editar produto' : 'Novo produto'}
        </h1>
      </div>

      <div className="lizzi-grid-2">
        <Card className="lizzi-section-card">
          <div className="lizzi-section-header">
            <div>
              <h2>Dados principais</h2>
              <p>Organize nome, categoria, imagem e preco.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="lizzi-form-grid-2">
              <div className="lizzi-grid-span-2">
                <ImageField
                  id="produto-imagem"
                  label="Imagem do produto"
                  previewUrl={form.imagem_base64 || (form.remover_imagem ? null : imagemAtual)}
                  onChange={handleImagemProduto}
                  onRemove={() => setForm((atual) => ({ ...atual, imagem_base64: null, remover_imagem: true }))}
                  helperText="Use uma foto clara do prato"
                  variant="wide"
                />
              </div>

              <div className="lizzi-grid-span-2">
                <Input label="Nome do produto" value={form.nome} onChange={handleChange('nome')} required />
              </div>

              <label className="lizzi-field-block lizzi-grid-span-2">
                <span className="lizzi-form-label">Descricao</span>
                <textarea
                  value={form.descricao}
                  onChange={handleChange('descricao')}
                  rows={4}
                  className="form-control lizzi-form-control lizzi-textarea"
                  placeholder="Descreva ingredientes ou diferenciais"
                />
              </label>

              <div>
                <Select
                  label="Categoria"
                  value={form.categoria_id}
                  onChange={handleChange('categoria_id')}
                  options={categorias.map((categoria) => ({ value: categoria.id, label: categoria.nome }))}
                  placeholder="Sem categoria"
                />
              </div>

              <div className="lizzi-inline-field-button">
                <Button type="button" variant="outline" onClick={handleNovaCategoria} fullWidth={false}>
                  <FiPlus size={15} style={{ marginRight: '6px' }} />
                  Nova categoria
                </Button>
              </div>

              <div className="lizzi-grid-span-2">
                <MoedaInput
                  label="Preco de venda"
                  value={form.preco_venda}
                  onValueChange={(valor) => setForm((atual) => ({ ...atual, preco_venda: valor }))}
                  required
                />
              </div>
            </div>

            <div className="lizzi-modal-actions" style={{ justifyContent: 'flex-start' }}>
              <Button type="submit" disabled={salvando} fullWidth={false}>
                {editando ? (salvando ? 'Salvando...' : 'Salvar produto') : salvando ? 'Criando...' : 'Criar produto'}
              </Button>
            </div>
          </form>
        </Card>

        <div className="lizzi-stack-gap">
          <div className="lizzi-stack-gap" style={{ gap: '12px' }}>
            <Kpi titulo="CMV" valor={formatarMoeda(cmvAoVivo)} tone="dourado" icone={<FiTrendingDown size={19} />} />
            <Kpi
              titulo="Margem"
              valor={formatarMoeda(margemLucroAoVivo)}
              tone={margemLucroAoVivo >= 0 ? 'sucesso' : 'erro'}
              icone={<FiTrendingUp size={19} />}
            />
            <Kpi
              titulo="Margem %"
              valor={margemPercentualAoVivo !== null ? `${margemPercentualAoVivo}%` : '—'}
              tone={margemPercentualAoVivo !== null && margemPercentualAoVivo >= 0 ? 'sucesso' : 'erro'}
              icone={<FiPercent size={19} />}
            />
          </div>

          <Card className="lizzi-section-card">
            <div className="lizzi-section-header">
              <div>
                <h2>Ficha tecnica</h2>
                <p>Monte a composicao do produto e acompanhe o custo.</p>
              </div>
            </div>

            {!editando ? (
              <div className="lizzi-locked-state">
                <FiLock size={18} />
                <p>Salve o produto primeiro para liberar a ficha tecnica.</p>
              </div>
            ) : (
              <>
                <div className="lizzi-ficha-lista">
                  {fichaTecnica.map((item, index) => {
                    const materiaPrima = materiasPrimas.find((mp) => String(mp.id) === String(item.materia_prima_id));
                    const subtotal = materiaPrima && item.quantidade ? Number(item.quantidade) * Number(materiaPrima.custo_unitario) : 0;

                    return (
                      <div key={index} className="lizzi-ficha-item">
                        <div className="lizzi-ficha-select">
                          <Select
                            label={index === 0 ? 'Materia-prima' : undefined}
                            value={item.materia_prima_id}
                            onChange={(e) => atualizarItemFicha(index, 'materia_prima_id', e.target.value)}
                            options={materiasPrimas.map((mp) => ({
                              value: mp.id,
                              label: `${mp.nome} (${mp.unidade_medida})`,
                            }))}
                            placeholder="Selecione"
                          />
                        </div>
                        <Input
                          label={index === 0 ? 'Quantidade' : undefined}
                          type="number"
                          step="0.0001"
                          min="0"
                          value={item.quantidade}
                          onChange={(e) => atualizarItemFicha(index, 'quantidade', e.target.value)}
                        />
                        <div className="lizzi-ficha-subtotal">
                          <span>Subtotal</span>
                          <strong>{formatarMoeda(subtotal)}</strong>
                        </div>
                        <button type="button" className="lizzi-icon-button" onClick={() => removerItemFicha(index)} aria-label="Remover item">
                          <FiTrash2 size={15} />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="lizzi-modal-actions" style={{ justifyContent: 'space-between' }}>
                  <Button type="button" variant="outline" onClick={adicionarItemFicha} fullWidth={false}>
                    <FiPlus size={15} style={{ marginRight: '6px' }} />
                    Adicionar item
                  </Button>
                  <Button type="button" onClick={handleSalvarFicha} disabled={salvandoFicha} fullWidth={false}>
                    {salvandoFicha ? 'Salvando...' : 'Salvar ficha tecnica'}
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
